import { useState, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TIERS } from '@/data/rewardsData';
import { TierIcons } from './TierIcons';
import { ChevronRight, Check } from 'lucide-react';

const tierGradients = {
  bloom: 'linear-gradient(145deg, hsl(195 50% 82% / 0.12), hsl(195 40% 88% / 0.05))',
  lustre: 'linear-gradient(145deg, hsl(40 76% 44% / 0.12), hsl(42 70% 55% / 0.05))',
  aura: 'linear-gradient(145deg, hsl(18 14% 38% / 0.15), hsl(18 12% 48% / 0.05))',
  luminary: 'linear-gradient(145deg, hsl(16 12% 25% / 0.2), hsl(18 14% 38% / 0.08))',
  leorglow: 'linear-gradient(145deg, hsl(16 18% 21% / 0.25), hsl(40 76% 44% / 0.08))',
};

const tierBorderColors = {
  bloom: 'hsl(195 50% 82% / 0.3)',
  lustre: 'hsl(40 76% 44% / 0.3)',
  aura: 'hsl(18 14% 38% / 0.4)',
  luminary: 'hsl(16 12% 25% / 0.5)',
  leorglow: 'hsl(40 76% 44% / 0.4)',
};

const tierTextColors = {
  bloom: 'hsl(195 50% 82%)',
  lustre: 'hsl(40 76% 52%)',
  aura: 'hsl(18 14% 58%)',
  luminary: 'hsl(30 15% 75%)',
  leorglow: 'hsl(40 76% 52%)',
};

const TierCard = ({ tier, index, isActive, onClick }) => {
  const cardRef = useRef(null);
  const TierIcon = TierIcons[tier.id];

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    cardRef.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${y}deg) scale(1.02)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'perspective(800px) rotateY(0deg) rotateX(0deg) scale(1)';
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        className="cursor-pointer"
        style={{ transition: 'transform 0.2s ease-out' }}
      >
        <Card
          className={`relative overflow-hidden rounded-2xl border p-6 lg:p-8 flex flex-col min-h-[360px] transition-all duration-500 ${
            isActive ? 'shadow-gold' : ''
          }`}
          style={{
            background: tierGradients[tier.id],
            borderColor: isActive ? tierBorderColors[tier.id] : 'hsl(var(--border) / 0.5)',
          }}
        >
          {/* Glow effect */}
          {isActive && (
            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${tierTextColors[tier.id]}, transparent)` }} />
          )}

          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              {/* Custom SVG Icon */}
              <div className="mb-3">
                {TierIcon && <TierIcon size={52} />}
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-foreground">{tier.name}</h3>
              <p className="font-body text-xs italic mt-1" style={{ color: tierTextColors[tier.id] }}>{tier.meaning}</p>
            </div>
            <Badge
              className="font-body text-xs px-3 py-1 rounded-full border-0"
              style={{ background: `${tierTextColors[tier.id]}20`, color: tierTextColors[tier.id] }}
            >
              {tier.earn}
            </Badge>
          </div>

          {/* Spend Range */}
          <div className="mb-4 pb-4 border-b border-border/30">
            <span className="font-body text-xs text-muted-foreground uppercase tracking-wider">Annual Spend</span>
            <p className="font-body text-sm text-foreground mt-1">{tier.spend}</p>
          </div>

          {/* Key benefits */}
          <div className="flex-1">
            <span className="font-body text-xs text-muted-foreground uppercase tracking-wider mb-3 block">Key Benefits</span>
            <ul className="space-y-2">
              {tier.benefits.slice(0, isActive ? 6 : 3).map((benefit, bi) => (
                <li key={bi} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tierTextColors[tier.id] }} />
                  <span className="font-body text-xs text-muted-foreground leading-relaxed">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-border/30 flex items-center justify-between">
            <span className="font-body text-sm font-medium" style={{ color: tierTextColors[tier.id] }}>
              {tier.discount} off treatments
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

export const TierShowcase = () => {
  const [activeTier, setActiveTier] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const ActiveTierIcon = TierIcons[TIERS[activeTier].id];

  return (
    <section id="tiers" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, hsl(var(--gold)), transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Royal ornamental divider */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-px w-16 bg-gold/20" />
          <svg width="16" height="16" viewBox="0 0 20 20" className="text-gold/40">
            <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
          </svg>
          <div className="h-px w-16 bg-gold/20" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4 block">Tier System</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight">
            Five Levels of <span className="text-shimmer-gold italic">Radiance</span>
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            Rise through our tier system — each level unlocks deeper discounts, exclusive perks, and elevated care.
          </p>
        </motion.div>

        {/* Tier progress bar with custom icons */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="hidden lg:flex items-center justify-center gap-0 mb-12 max-w-3xl mx-auto"
          style={{ transformOrigin: 'left' }}
        >
          {TIERS.map((tier, i) => {
            const Icon = TierIcons[tier.id];
            return (
              <div key={tier.id} className="flex items-center flex-1">
                <button
                  onClick={() => setActiveTier(i)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 z-10 ${
                    i <= activeTier ? 'shadow-gold scale-110' : 'opacity-50'
                  }`}
                  style={{
                    background: i <= activeTier ? `${tierTextColors[tier.id]}15` : 'hsl(var(--muted))',
                    border: i <= activeTier ? `1px solid ${tierTextColors[tier.id]}40` : '1px solid transparent',
                  }}
                >
                  <Icon size={28} />
                </button>
                {i < TIERS.length - 1 && (
                  <div className="flex-1 h-px mx-1" style={{ background: i < activeTier ? `${tierTextColors[TIERS[i + 1].id]}` : 'hsl(var(--border))' }} />
                )}
              </div>
            );
          })}
        </motion.div>

        {/* Tier Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
          {TIERS.map((tier, i) => (
            <TierCard
              key={tier.id}
              tier={tier}
              index={i}
              isActive={activeTier === i}
              onClick={() => setActiveTier(i)}
            />
          ))}
        </div>

        {/* Active Tier Expanded Details */}
        <motion.div
          key={activeTier}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8 lg:mt-12"
        >
          <Card className="bg-gradient-card border-border/50 rounded-2xl p-8 lg:p-10">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  {ActiveTierIcon && <ActiveTierIcon size={56} />}
                  <div>
                    <h3 className="font-display text-2xl text-foreground">{TIERS[activeTier].name}</h3>
                    <p className="font-body text-sm italic" style={{ color: tierTextColors[TIERS[activeTier].id] }}>{TIERS[activeTier].meaning}</p>
                  </div>
                </div>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Annual spend: <span className="text-foreground">{TIERS[activeTier].spend}</span> · Earn rate: <span style={{ color: tierTextColors[TIERS[activeTier].id] }}>{TIERS[activeTier].earnLabel}</span> · Discount: <span style={{ color: tierTextColors[TIERS[activeTier].id] }}>{TIERS[activeTier].discount}</span>
                </p>
              </div>
              <div className="flex-1">
                <h4 className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-3">All Benefits</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TIERS[activeTier].benefits.map((b, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tierTextColors[TIERS[activeTier].id] }} />
                      <span className="font-body text-xs text-muted-foreground leading-relaxed">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
